import { Injectable } from '@angular/core';
import * as localforage from 'localforage';
import { cloneDeep, without, uniq, get, filter, map } from 'lodash';
import { EntityServiceProvider } from '../datasource';
import { DEFAULT_PAGE_SIZE } from '@config/index';
import { promiseMap } from '@shared/utils';
import { isAlwaysVisiblePredicate } from '@containers/base/list/xux-list.column-routines';

export const LIST_VIEW_NAMESPACE = 'xvi_ui_list_view';

@Injectable()
export class ListViewService {
  public defaultViewName: string = '__default__';
  public namespace: string = 'xvi_ui_list_view';

  public constructor(public entityServiceProvider: EntityServiceProvider) {}

  public async saveListView(
    entityType: string,
    viewName: string,
    data,
  ): Promise<any> {
    if (viewName !== this.defaultViewName) {
      this.setListViewName(viewName, entityType);
      data.viewName = viewName;
    }

    const listViewSaveResult = await localforage.setItem(
      `${LIST_VIEW_NAMESPACE}_${entityType}_${viewName}`,
      data,
    );

    return listViewSaveResult;
  }

  public async restoreListView(
    entityType: string,
    viewName: string,
  ): Promise<any> {
    const listView = await localforage.getItem(
      `${LIST_VIEW_NAMESPACE}_${entityType}_${viewName}`,
    );

    return listView;
  }

  public async removeListView(
    entityType: string,
    viewName: string,
  ): Promise<any> {
    this.removeListViewName(viewName, entityType);

    const listViewRemoveResult = await localforage.removeItem(
      `${LIST_VIEW_NAMESPACE}_${entityType}_${viewName}`,
    );

    return listViewRemoveResult;
  }

  public saveDefaultListView(entityType: string, data): Promise<any> {
    return this.saveListView(entityType, this.defaultViewName, data);
  }

  public restoreDefaultListView(entityType: string): Promise<any> {
    return this.restoreListView(entityType, this.defaultViewName);
  }

  public async removeDefaultListViews(): Promise<any> {
    const keys: string[] = await localforage.keys();
    const itemsToDelete: string[] = filter(
      keys,
      (key: string) =>
        key.startsWith(`${this.namespace}`) &&
        key.endsWith(`${this.defaultViewName}`),
    );

    await promiseMap(itemsToDelete, (key) => localforage.removeItem(key));
  }

  public composeDefaultView(entityType: string): object {
    const serviceClass = this.entityServiceProvider.getServiceClass(entityType);
    const columnConfig = get(serviceClass, 'config.list.columns', []);
    const selectedColumns = map(
      filter(columnConfig, isAlwaysVisiblePredicate),
      'path',
    );

    return {
      selectedColumns,
      entity_type: entityType,
      filterModel: {},
      sortModel: [],
      pageSize: DEFAULT_PAGE_SIZE,
      quickFilter: '',
      currentPage: 1,
    };
  }

  public async getListViewNames(entityType: string): Promise<any> {
    const listViewNames = await localforage.getItem(
      `${LIST_VIEW_NAMESPACE}_${entityType}`,
    );

    return listViewNames;
  }

  public async setListViewName(
    listViewName: string,
    entityType: string,
  ): Promise<any> {
    const key = `${LIST_VIEW_NAMESPACE}_${entityType}`;
    const result = await localforage.getItem(key);
    const newListViewNames = cloneDeep(result || []) as any[];

    newListViewNames.push(listViewName);
    const setResult = await localforage.setItem(key, uniq(newListViewNames));

    return setResult;
  }

  public async removeListViewName(
    listViewName: string,
    entityType: string,
  ): Promise<any> {
    const key = `${LIST_VIEW_NAMESPACE}_${entityType}`;
    const result = await localforage.getItem(key);
    const newListViewNames = cloneDeep(result || []);
    const value = without(newListViewNames as any[], listViewName);
    const setResult = await localforage.setItem(key, value);

    return setResult;
  }
}
